import type {
  AppointmentCardAssignee,
  AppointmentCardBanner,
  AppointmentCardMetric,
  AppointmentCardModel,
  AppointmentCardTone,
} from '@/components/work-items/AppointmentCard';
import {
  buildAppointmentCardPresentation,
  getAppointmentPriorityPresentation,
} from '@/lib/appointmentCardPresentation';
import { getAppointmentCardOutlineClasses } from '@/lib/appointmentStatusVisual';
import { formatDateSAST, formatTimeRangeSAST, formatTimeSAST } from '@/lib/sastTime';
import { getAppointmentDisplayNumberWithParent } from '@/lib/appointmentDisplay';

import type { WorkItem } from '../types';
import {
  getWorkItemAppointmentScheduleVarianceInfo,
  getWorkItemAppointmentTimingInfo,
  getWorkItemExactWorkflowStatus,
  getWorkItemPanelStatusInfo,
  type WorkItemPanelStatusInfo,
  type WorkItemStatusContext,
} from './workItemStatusPresentation';
import { getWorkItemAssignedParticipants, getWorkItemPrimaryAssigneeName } from './workItemOwnership';

type SmartTaskCardLike = Pick<
  WorkItem,
  | 'id'
  | 'item_type'
  | 'item_number'
  | 'title'
  | 'status'
  | 'priority'
  | 'scheduled_start'
  | 'scheduled_end'
  | 'contact_name'
  | 'location_name'
  | 'location_address'
  | 'description'
  | 'notes'
  | 'custom_fields'
  | 'started_at'
  | 'assigned_to'
  | 'assignees'
> & {
  parent_id?: string | number | null;
};

type SmartTaskAppointmentLookupItem = Pick<WorkItem, 'id' | 'item_number'>;

function normalizeStatus(value?: string | null): string {
  return String(value || '').trim().toLowerCase();
}

function getDerivedSmartTaskStatusPresentation(
  exactStatus: string,
  statusInfo: WorkItemPanelStatusInfo,
  timingStatus: ReturnType<typeof buildAppointmentCardPresentation>['timingStatus'],
): { label: string | null; tone?: AppointmentCardTone } {
  const normalizedExactStatus = normalizeStatus(exactStatus);

  if (normalizedExactStatus === 'paused') {
    return { label: 'Paused', tone: 'amber' };
  }

  if (normalizedExactStatus === 'travel_paused') {
    return { label: 'Travel Paused', tone: 'amber' };
  }

  if (['checked_in', 'working', 'in_progress'].includes(normalizedExactStatus)) {
    return timingStatus === 'over_schedule'
      ? { label: 'In Progress', tone: 'green' }
      : { label: null, tone: 'green' };
  }

  if (normalizedExactStatus === 'completing') {
    return timingStatus === 'over_schedule'
      ? { label: 'Completing', tone: 'green' }
      : { label: 'Completing', tone: 'green' };
  }

  if (['arrived', 'on_site', 'current'].includes(normalizedExactStatus)) {
    return timingStatus === 'over_schedule'
      ? { label: 'At Site', tone: 'teal' }
      : { label: null, tone: 'teal' };
  }

  if (['traveling', 'en_route', 'on_route'].includes(normalizedExactStatus)) {
    return { label: null, tone: 'blue' };
  }

  const timingStatusHandlesLabel = ['onroute', 'onroute_late', 'onsite_arrived', 'onsite_working'].includes(timingStatus);
  if (timingStatusHandlesLabel) {
    return { label: null };
  }

  return {
    label: statusInfo.label,
    tone: getSmartTaskStatusTone(statusInfo),
  };
}

function getDerivedSmartTaskTimer(
  item: SmartTaskCardLike,
  exactStatus: string,
  timingStatus: ReturnType<typeof buildAppointmentCardPresentation>['timingStatus'],
): { startedAt?: string | null; tone?: AppointmentCardTone } {
  const normalizedExactStatus = normalizeStatus(exactStatus);
  if (!item.started_at || !['checked_in', 'working', 'in_progress', 'completing'].includes(normalizedExactStatus)) {
    return {};
  }

  return {
    startedAt: item.started_at,
    tone: timingStatus === 'over_schedule' ? 'orange' : 'green',
  };
}

function buildAssigneeList(
  item: SmartTaskCardLike,
  assigneesOverride?: AppointmentCardAssignee[],
): AppointmentCardAssignee[] | undefined {
  if (assigneesOverride && assigneesOverride.length > 0) {
    return assigneesOverride;
  }

  const derivedAssignees = getWorkItemAssignedParticipants(item as WorkItem).map((participant) => ({
    id: participant.id,
    name: participant.name,
    isPrimary: participant.isPrimary,
  }));

  return derivedAssignees.length > 0 ? derivedAssignees : undefined;
}

function mapWorkflowStatusForTiming(status: string): string {
  switch (status) {
    case 'traveling':
    case 'travel_paused':
      return 'en_route';
    case 'paused':
      return 'in_progress';
    case 'current':
      return 'on_site';
    default:
      return status;
  }
}

function buildTimingPresentationInput(item: SmartTaskCardLike, statusForTiming: string): SmartTaskCardLike {
  if (!item.custom_fields || typeof item.custom_fields !== 'object' || Array.isArray(item.custom_fields)) {
    return {
      ...item,
      status: statusForTiming,
    };
  }

  const normalizedStatus = normalizeStatus(statusForTiming);
  const customFields = { ...(item.custom_fields as Record<string, unknown>) };
  const isTravelTimingStatus = ['traveling', 'travel_paused', 'en_route', 'on_route'].includes(normalizedStatus);
  const isOnSiteTimingStatus = isTravelTimingStatus || ['arrived', 'on_site', 'current', 'checked_in', 'working', 'in_progress', 'paused', 'completing'].includes(normalizedStatus);
  const isWorkingTimingStatus = ['checked_in', 'working', 'in_progress', 'paused', 'completing'].includes(normalizedStatus);

  if (!isTravelTimingStatus) {
    delete customFields.travel_started_at;
  }

  if (!isOnSiteTimingStatus) {
    delete customFields.arrived_at;
  }

  if (isWorkingTimingStatus) {
    if (!customFields.check_in_timestamp && item.started_at) {
      customFields.check_in_timestamp = item.started_at;
    }
  } else {
    delete customFields.check_in_timestamp;
  }

  return {
    ...item,
    status: statusForTiming,
    custom_fields: customFields,
  };
}

/**
 * Header banner for appointment cards — aligned with SchedulePanel.getScheduleCardBanner.
 * When present, chip row timing/status labels are suppressed to avoid duplicating the header.
 */
function buildIntrinsicSmartTaskBanner(
  item: SmartTaskCardLike,
  exactStatus: string,
  statusInfo: WorkItemPanelStatusInfo,
  context: WorkItemStatusContext,
): AppointmentCardBanner | null {
  if (normalizeStatus(item.item_type) !== 'appointment') {
    return null;
  }

  const timingInfo = getWorkItemAppointmentTimingInfo(item as WorkItem, context);
  const scheduleVariance = getWorkItemAppointmentScheduleVarianceInfo(item as WorkItem, context);
  const startTime = item.scheduled_start ? formatTimeSAST(item.scheduled_start) : '';
  const endTime = item.scheduled_end ? formatTimeSAST(item.scheduled_end) : null;
  const timeRange = endTime ? `${startTime}-${endTime}` : startTime;
  const sk = normalizeStatus(exactStatus);

  if (scheduleVariance.isAllocatedTimeExceeded || statusInfo.isOverSchedule) {
    const exceededMinutes = scheduleVariance.allocatedTimeExceededMinutes;
    return {
      label: 'Allocated Time Exceeded',
      value: exceededMinutes > 0
        ? (exceededMinutes >= 60
          ? `${(exceededMinutes / 60).toFixed(1).replace(/\.0$/, '')}h`
          : `${exceededMinutes}m`)
        : (endTime ? `Ends ${endTime}` : 'Past due'),
      tone: 'orange',
    };
  }

  if (scheduleVariance.isBehindSchedule) {
    const lateValue = scheduleVariance.behindScheduleMinutes >= 60
      ? `${(scheduleVariance.behindScheduleMinutes / 60).toFixed(1).replace(/\.0$/, '')}h`
      : `${scheduleVariance.behindScheduleMinutes}m`;
    return {
      label: 'Behind Schedule',
      value: lateValue,
      tone: 'orange',
    };
  }

  if (timingInfo.isRunningLate) {
    const lateValue = timingInfo.lateMinutes >= 60
      ? `${(timingInfo.lateMinutes / 60).toFixed(1).replace(/\.0$/, '')}h`
      : `${timingInfo.lateMinutes}m`;
    const isTravelStatus = ['traveling', 'travel_paused', 'en_route', 'on_route'].includes(sk);
    return {
      label: isTravelStatus
        ? (sk === 'travel_paused' ? 'Travel Paused - Running Late' : 'On Route - Running Late')
        : 'Running Late',
      value: lateValue,
      tone: 'red',
    };
  }

  if (['traveling', 'travel_paused', 'en_route', 'on_route'].includes(sk)) {
    return {
      label: sk === 'travel_paused' ? 'Travel Paused' : 'On Route',
      value: timeRange || undefined,
      tone: 'blue',
    };
  }

  if (['checked_in', 'working', 'in_progress', 'completing', 'paused'].includes(sk)) {
    return {
      label: sk === 'paused' ? 'Paused' : 'In Progress',
      value: timeRange || undefined,
      tone: sk === 'paused' ? 'amber' : 'green',
    };
  }

  if (statusInfo.isTerminal || statusInfo.key === 'closed') {
    return {
      label: 'Closed',
      value: timeRange || undefined,
      tone: 'gray',
    };
  }

  return {
    label: statusInfo.label || 'Scheduled',
    value: timeRange || undefined,
    tone: 'blue',
  };
}

export function getSmartTaskStatusTone(statusInfo: WorkItemPanelStatusInfo): AppointmentCardTone {
  switch (statusInfo.key) {
    case 'cancelled':
    case 'missed':
    case 'unresolved':
      return 'red';
    case 'over_schedule':
      return 'orange';
    case 'travel_paused':
    case 'paused':
      return 'amber';
    case 'traveling':
    case 'en_route':
    case 'on_route':
      return 'blue';
    case 'arrived':
    case 'on_site':
    case 'current':
      return 'teal';
    case 'checked_in':
    case 'working':
    case 'in_progress':
    case 'completing':
    case 'resolved':
      return 'green';
    case 'dispatched':
      return 'purple';
    case 'scheduled':
    case 'assigned':
    case 'confirmed':
    case 'pending':
      return 'blue';
    default:
      return statusInfo.isTerminal ? 'gray' : 'blue';
  }
}

export interface BuildSmartTaskAppointmentCardOptions {
  context?: WorkItemStatusContext;
  parentJob?: WorkItem | null;
  itemsById?: Map<string, SmartTaskAppointmentLookupItem>;
  itemNumber?: string | null;
  groupNumber?: string | null;
  groupLeaderName?: string | null;
  isGroupLeader?: boolean;
  assignees?: AppointmentCardAssignee[];
  assigneeName?: string | null;
  contactName?: string | null;
  locationLabel?: string | null;
  description?: string | null;
  compactDescription?: string | null;
  metrics?: AppointmentCardMetric[];
  banner?: AppointmentCardBanner | null;
  tooltipLines?: string[];
  statusLabel?: string | null;
  statusTone?: AppointmentCardTone;
  timingLabel?: string | null;
  timingTone?: AppointmentCardTone;
  priorityLabel?: string | null;
  priorityTone?: AppointmentCardTone;
  title?: string | null;
  scheduledStart?: string | null;
  scheduledEnd?: string | null;
  scheduleLabel?: string | null;
  scheduleDetailLabel?: string | null;
}

export interface SmartTaskAppointmentCardResult {
  model: AppointmentCardModel;
  cardClassName: string;
  exactStatus: string;
  statusInfo: WorkItemPanelStatusInfo;
}

function stripBannerEmoji(value?: string | null): string | null {
  const normalized = String(value || '')
    .replace(/[\u{1F300}-\u{1FAFF}\u2600-\u27BF]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();

  return normalized.length > 0 ? normalized : null;
}

function normalizeBannerLabel(value?: string | null): string | null {
  const normalized = stripBannerEmoji(value);

  switch (normalized) {
    case 'Working':
      return 'Active';
    case 'On Route Late':
    case 'On Route · Late':
    case 'On Route - Running Late':
      return 'Running Late';
    case 'Travel Paused - Running Late':
      return 'Running Late';
    case 'Running Late':
      return 'Running Late';
    case 'Over Schedule':
      return 'Allocated Time Exceeded';
    case 'Today':
    case 'Future':
      return 'Scheduled';
    default:
      return normalized;
  }
}

function buildSmartTaskScheduleLabel(start?: string | null, end?: string | null): string | null {
  if (!start) {
    return null;
  }

  const dateLabel = formatDateSAST(start, { day: '2-digit', month: 'short' });
  const timeLabel = end ? formatTimeRangeSAST(start, end) : formatTimeSAST(start);
  return [timeLabel, dateLabel].filter(Boolean).join(' · ');
}

export function buildSmartTaskDefaultBanner(
  card: Pick<SmartTaskAppointmentCardResult, 'model'>,
  options?: {
    fallbackLabel?: string | null;
    fallbackTone?: AppointmentCardTone;
    value?: string | null;
  },
): AppointmentCardBanner {
  const timingLabel = normalizeBannerLabel(card.model.timingLabel);
  const statusLabel = normalizeBannerLabel(card.model.statusLabel);
  const fallbackLabel = normalizeBannerLabel(options?.fallbackLabel) || 'Scheduled';
  const label = timingLabel || statusLabel || fallbackLabel;
  const value = stripBannerEmoji(options?.value)
    || (statusLabel && statusLabel !== label ? statusLabel : null)
    || card.model.scheduleDetailLabel
    || card.model.scheduleLabel
    || null;

  return {
    label,
    value: value || undefined,
    tone: card.model.timingTone || card.model.statusTone || options?.fallbackTone || 'blue',
  };
}

export function buildSmartTaskPanelBanner(
  card: Pick<SmartTaskAppointmentCardResult, 'model' | 'statusInfo'>,
  options?: {
    fallbackLabel?: string | null;
    fallbackTone?: AppointmentCardTone;
    value?: string | null;
  },
): AppointmentCardBanner {
  if (card.statusInfo.key === 'closed') {
    const value = stripBannerEmoji(options?.value)
      || card.model.scheduleDetailLabel
      || card.model.scheduleLabel
      || null;

    return {
      label: 'Closed',
      value: value || undefined,
      tone: 'gray',
    };
  }

  return buildSmartTaskDefaultBanner(card, options);
}

export function buildSmartTaskAppointmentCard(
  item: SmartTaskCardLike,
  options: BuildSmartTaskAppointmentCardOptions = {},
): SmartTaskAppointmentCardResult {
  const {
    context = {},
    parentJob = null,
    itemsById,
    itemNumber,
    groupNumber = null,
    groupLeaderName = null,
    isGroupLeader = false,
    assignees,
    assigneeName,
    contactName,
    locationLabel,
    description,
    compactDescription,
    metrics,
    banner,
    tooltipLines,
    statusLabel,
    statusTone,
    timingLabel,
    timingTone,
    priorityLabel,
    priorityTone,
    title,
    scheduledStart,
    scheduledEnd,
    scheduleLabel,
    scheduleDetailLabel,
  } = options;

  const exactStatus = getWorkItemExactWorkflowStatus(item as WorkItem, context);
  const statusInfo = getWorkItemPanelStatusInfo(item as WorkItem, context);
  const scheduleVariance = getWorkItemAppointmentScheduleVarianceInfo(item as WorkItem, context);
  const statusForTiming = mapWorkflowStatusForTiming(exactStatus);
  const timingPresentation = buildAppointmentCardPresentation({
    ...buildTimingPresentationInput(item, statusForTiming),
    scheduled_start: scheduledStart ?? item.scheduled_start,
    scheduled_end: scheduledEnd ?? item.scheduled_end,
  }, false);
  const priorityPresentation = getAppointmentPriorityPresentation(item.priority);
  const derivedStatusPresentation = getDerivedSmartTaskStatusPresentation(
    exactStatus,
    statusInfo,
    timingPresentation.timingStatus,
  );
  const derivedTimer = getDerivedSmartTaskTimer(item, exactStatus, timingPresentation.timingStatus);
  const resolvedStatusTone = statusTone ?? derivedStatusPresentation.tone ?? getSmartTaskStatusTone(statusInfo);
  const resolvedTimingTone = timingTone ?? (
    scheduleVariance.isAllocatedTimeExceeded
      ? 'orange'
      : scheduleVariance.isBehindSchedule
        ? 'orange'
        : (statusInfo.isOverSchedule ? 'orange' : timingPresentation.timingTone)
  );
  const resolvedTimingLabel = timingLabel ?? (exactStatus === 'travel_paused'
    ? '⏸ Travel Paused'
    : timingPresentation.timingLabel);
  const resolvedStatusLabel = statusLabel ?? derivedStatusPresentation.label;
  const resolvedItemNumber = itemNumber ?? (
    item.item_type === 'appointment'
      ? getAppointmentDisplayNumberWithParent(item as WorkItem, {
          parentJob: parentJob || undefined,
          itemsById,
        })
      : (item.item_number || null)
  );
  const resolvedAssignees = buildAssigneeList(item, assignees);
  const resolvedScheduledStart = scheduledStart ?? item.scheduled_start;
  const resolvedScheduledEnd = scheduledEnd ?? item.scheduled_end;
  const resolvedScheduleLabel = scheduleLabel ?? buildSmartTaskScheduleLabel(resolvedScheduledStart, resolvedScheduledEnd);
  const resolvedScheduleDetailLabel = scheduleDetailLabel ?? (resolvedScheduledStart && resolvedScheduledEnd
    ? formatTimeRangeSAST(resolvedScheduledStart, resolvedScheduledEnd)
    : null);

  const intrinsicBanner = buildIntrinsicSmartTaskBanner(item, exactStatus, statusInfo, context);
  const resolvedBanner = banner ?? intrinsicBanner;
  const suppressStatusChips = Boolean(resolvedBanner);
  const outlineTone: AppointmentCardTone = resolvedBanner?.tone ?? resolvedTimingTone;

  return {
    exactStatus,
    statusInfo,
    cardClassName: getAppointmentCardOutlineClasses(outlineTone),
    model: {
      id: String(item.id),
      title: title || item.title || resolvedItemNumber || 'Untitled work item',
      itemNumber: resolvedItemNumber,
      groupNumber,
      isGroupLeader,
      groupLeaderName,
      parentJobNumber: parentJob?.item_number || null,
      timingLabel: suppressStatusChips ? null : resolvedTimingLabel,
      timingTone: resolvedTimingTone,
      statusLabel: suppressStatusChips ? null : resolvedStatusLabel,
      statusTone: resolvedStatusTone,
      priorityLabel: priorityLabel ?? priorityPresentation.label,
      priorityTone: priorityTone ?? priorityPresentation.tone,
      scheduledStart: resolvedScheduledStart,
      scheduledEnd: resolvedScheduledEnd,
      scheduleLabel: resolvedScheduleLabel,
      scheduleDetailLabel: resolvedScheduleDetailLabel,
      runningTimerStartedAt: derivedTimer.startedAt,
      runningTimerTone: derivedTimer.tone,
      assigneeName: assigneeName ?? getWorkItemPrimaryAssigneeName(item as WorkItem) ?? null,
      assignees: resolvedAssignees,
      contactName: contactName ?? item.contact_name ?? null,
      locationLabel: locationLabel ?? item.location_name ?? item.location_address ?? null,
      description: description ?? item.description ?? item.notes ?? null,
      compactDescription: compactDescription ?? item.location_name ?? item.location_address ?? item.description ?? item.notes ?? null,
      banner: resolvedBanner ?? undefined,
      metrics,
      tooltipLines,
    },
  };
}